import React from 'react';
import { Container, Row, Col, Card, Spinner, Badge, Table, Button } from 'react-bootstrap';
import {
  TruckIcon, CakeIcon, ArrowLeftIcon, PhoneIcon,
  MapPinIcon, ClipboardDocumentListIcon, CheckCircleIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { useTodaysDelivery } from './hooks/useTodaysDelivery';

const TodaysDelivery: React.FC = () => {
  const {
    data, loading, deliveringId, isExpired,
    handleMarkDelivered, fmt, statusColor,
    formatDate, deliveredCount, navigateToDashboard,
  } = useTodaysDelivery();

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {/* Back button + Title */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <Button variant="light" size="sm" onClick={navigateToDashboard}
            style={{ borderRadius: '8px' }}>
            <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          </Button>
          <div>
            <h4 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>
              <TruckIcon style={{ width: '24px', height: '24px', color: '#3b82f6', marginRight: '8px' }} />
              Today's Deliveries
            </h4>
            {data && <small className="text-muted">{formatDate(data.date)}</small>}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
        ) : !data || data.total_orders === 0 ? (
          <Card className="border-0 shadow-sm text-center py-5" style={{ borderRadius: '16px' }}>
            <Card.Body>
              <TruckIcon style={{ width: '48px', height: '48px', color: '#d1d5db', marginBottom: '12px' }} />
              <h5 className="text-muted">No deliveries scheduled for today</h5>
              <p className="text-muted mb-0">Orders with today's delivery date will appear here.</p>
            </Card.Body>
          </Card>
        ) : (
          <>
            {/* Overview stats bar */}
            <Row className="g-2 mb-3">
              <Col xs={4}>
                <Card className="border-0 shadow-sm text-center mobile-stat-card"
                  style={{ borderRadius: '12px', background: '#eff6ff' }}>
                  <Card.Body className="p-2">
                    <ClipboardDocumentListIcon style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
                    <div className="fw-bold" style={{ fontSize: '1.2rem', color: '#1e40af' }}>{data.total_orders}</div>
                    <div style={{ fontSize: '0.7rem', color: '#1e40af' }}>Orders</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={4}>
                <Card className="border-0 shadow-sm text-center mobile-stat-card"
                  style={{ borderRadius: '12px', background: '#ecfdf5' }}>
                  <Card.Body className="p-2">
                    <CakeIcon style={{ width: '20px', height: '20px', color: '#10b981' }} />
                    <div className="fw-bold" style={{ fontSize: '1.2rem', color: '#065f46' }}>
                      {data.product_summary.reduce((sum, c) => sum + c.total_items, 0)}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#065f46' }}>Total Items</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={4}>
                <Card className="border-0 shadow-sm text-center mobile-stat-card"
                  style={{ borderRadius: '12px', background: deliveredCount === data.delivery_list.length ? '#ecfdf5' : '#fefce8' }}>
                  <Card.Body className="p-2">
                    <CheckCircleIcon style={{ width: '20px', height: '20px', color: deliveredCount === data.delivery_list.length ? '#10b981' : '#eab308' }} />
                    <div className="fw-bold" style={{ fontSize: '1.2rem', color: deliveredCount === data.delivery_list.length ? '#065f46' : '#854d0e' }}>
                      {deliveredCount}/{data.delivery_list.length}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: deliveredCount === data.delivery_list.length ? '#065f46' : '#854d0e' }}>Delivered</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Product Summary by Category */}
            <h5 className="fw-bold mb-2" style={{ color: '#1f2937' }}>
              <CakeIcon style={{ width: '20px', height: '20px', color: '#3b82f6', marginRight: '6px' }} />
              Delivery Totals by Category
            </h5>
            <Row className="g-2 mb-4">
              {data.product_summary.map((cat) => (
                <Col key={cat.category_name} xs={12} md={6} lg={4}>
                  <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-bold mb-0" style={{ color: '#1f2937' }}>{cat.category_name}</h6>
                        <Badge bg="primary" pill>{cat.total_items} total</Badge>
                      </div>
                      <Table size="sm" className="mb-0" style={{ fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ color: '#6b7280' }}>
                            <th style={{ border: 'none', fontWeight: 600 }}>Product</th>
                            <th style={{ border: 'none', fontWeight: 600, textAlign: 'right' }}>Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cat.products.map((prod) => (
                            <tr key={prod.product_name}>
                              <td style={{ border: 'none', padding: '4px 0', color: '#374151' }}>{prod.product_name}</td>
                              <td style={{ border: 'none', padding: '4px 0', textAlign: 'right', fontWeight: 700, color: '#1f2937' }}>
                                {prod.total_quantity} <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '0.75rem' }}>{prod.unit}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Delivery List */}
            <h5 className="fw-bold mb-2" style={{ color: '#1f2937' }}>
              <TruckIcon style={{ width: '20px', height: '20px', color: '#3b82f6', marginRight: '6px' }} />
              Delivery List
            </h5>
            <Row className="g-2">
              {data.delivery_list.map((order) => (
                <Col key={order.order_number} xs={12} md={6}>
                  <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-start justify-content-between mb-2">
                        <div>
                          <h6 className="fw-bold mb-0" style={{ color: '#1f2937' }}>{order.customer_name}</h6>
                          <small className="text-muted">{order.order_number}</small>
                        </div>
                        <div className="text-end">
                          <span style={{ background: statusColor[order.status] || '#6b7280', color: '#fff', fontSize: '0.7rem', borderRadius: '6px', padding: '2px 8px', display: 'inline-block' }}>
                            {order.status}
                          </span>
                          <div className="fw-bold mt-1" style={{ fontSize: '0.9rem', color: '#1f2937' }}>
                            {fmt(order.total_amount)}
                          </div>
                        </div>
                      </div>
                      {(order.customer_phone || order.customer_address) && (
                        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '8px' }}>
                          {order.customer_phone && (
                            <div className="d-flex align-items-center gap-1">
                              <PhoneIcon style={{ width: '12px', height: '12px' }} />
                              <span>{order.customer_phone}</span>
                            </div>
                          )}
                          {order.customer_address && (
                            <div className="d-flex align-items-center gap-1">
                              <MapPinIcon style={{ width: '12px', height: '12px' }} />
                              <span>{order.customer_address}{order.customer_city ? `, ${order.customer_city}` : ''}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '8px 10px' }}>
                        {order.items.map((item) => (
                          <div key={item.product_name} className="d-flex justify-content-between" style={{ fontSize: '0.8rem', color: '#374151' }}>
                            <span>{item.product_name}</span>
                            <span className="fw-bold">{item.quantity} {item.unit}</span>
                          </div>
                        ))}
                      </div>
                      {order.notes && (
                        <div className="mt-2" style={{ fontSize: '0.78rem', color: '#1e40af', background: '#eff6ff', borderRadius: '6px', padding: '6px 8px' }}>
                          <strong>Note:</strong> {order.notes}
                        </div>
                      )}
                      <div className="mt-2 text-end">
                        {order.status === 'delivered' ? (
                          <span className="d-inline-flex align-items-center gap-1" style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>
                            <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                            Delivered
                          </span>
                        ) : (
                          <Button size="sm" variant="success" disabled={deliveringId === order.order_id || isExpired}
                            onClick={() => handleMarkDelivered(order.order_id)}
                            style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem' }}>
                            {deliveringId === order.order_id ? (
                              <Spinner animation="border" size="sm" className="me-1" />
                            ) : (
                              <CheckCircleIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                            )}
                            Mark Delivered
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}
      </Container>
    </CompanyLayout>
  );
};

export default TodaysDelivery;
